import { useLocation } from 'react-router-dom';
import { siteInfo } from '@/mocks/data';

interface SEOPageMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
}

export default function SEOPageMeta({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  ogImage,
}: SEOPageMetaProps) {
  const baseUrl = 'https://www.aiqingdao.asia';
  const canonicalUrl = canonicalPath ? `${baseUrl}${canonicalPath}` : baseUrl;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}

export function OrganizationSchema() {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: siteInfo.name,
          url: 'https://www.aiqingdao.asia/',
          description: siteInfo.description,
          sameAs: Object.values(siteInfo.socialMedia),
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: siteInfo.contactPhone,
            contactType: 'customer service',
            availableLanguage: ['Chinese'],
          },
        }),
      }}
    />
  );
}

interface BreadcrumbSchemaItem {
  name: string;
  item?: string;
}

export function BreadcrumbListSchema({ items }: { items: BreadcrumbSchemaItem[] }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.item || undefined,
          })),
        }),
      }}
    />
  );
}

export function WebSiteSchema() {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: siteInfo.name,
          url: 'https://www.aiqingdao.asia/',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.aiqingdao.asia/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }),
      }}
    />
  );
}

export function WebPageSchema({
  title,
  description,
  url,
}: {
  title: string;
  description: string;
  url: string;
}) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: title,
          description: description,
          url: url,
          isPartOf: {
            '@type': 'WebSite',
            name: siteInfo.name,
            url: 'https://www.aiqingdao.asia/',
          },
        }),
      }}
    />
  );
}

export function ArticleSchema({
  headline,
  description,
  url,
  datePublished,
  author,
  image,
}: {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  author: string;
  image?: string;
}) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: headline,
          description: description,
          url: url,
          datePublished: datePublished,
          author: {
            '@type': 'Person',
            name: author,
          },
          publisher: {
            '@type': 'Organization',
            name: siteInfo.name,
          },
          image: image || undefined,
        }),
      }}
    />
  );
}

export function FAQPageSchema({
  questions,
}: {
  questions: { question: string; answer: string }[];
}) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: questions.map((q) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: q.answer,
            },
          })),
        }),
      }}
    />
  );
}